import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Trash2, UserPlus, Users as UsersIcon, Cpu } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';
import { Card, CardBody, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AssignDevicesModal } from '@/components/users/AssignDevicesModal';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/useToast';
import type { Role, User } from '@/lib/types';

export function UsersPage() {
  const { data, isLoading } = useUsers();
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole]         = useState<Role>('operator');
  const [assignTarget, setAssignTarget] = useState<User | null>(null);

  const addUser = useMutation({
    mutationFn: () => api('/api/auth/users', { method: 'POST', body: { username, password, role } }),
    onSuccess: () => {
      setUsername(''); setPassword(''); setRole('operator');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.ok('User added');
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to add user'),
  });

  const deleteUser = useMutation({
    mutationFn: (id: number) => api(`/api/auth/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.ok('User deleted');
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to delete'),
  });

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-ink tracking-tight">Users</h1>
        <p className="text-sm text-slate-500 mt-0.5">Create accounts, assign roles, and pick which devices each user can see.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <Card>
          <CardBody>
            <CardTitle className="mb-3">All users</CardTitle>
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
              </div>
            ) : (data ?? []).length === 0 ? (
              <EmptyState icon={<UsersIcon className="w-6 h-6" />} title="No users yet" />
            ) : (
              <div className="overflow-x-auto -mx-5">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-5 py-2">Username</th>
                      <th className="text-left px-5 py-2">Role</th>
                      <th className="text-left px-5 py-2">Created</th>
                      <th className="text-right px-5 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data ?? []).map((u: User) => (
                      <tr key={u.id} className="border-b border-slate-100">
                        <td className="px-5 py-2.5 font-semibold text-ink">{u.username}</td>
                        <td className="px-5 py-2.5">
                          <Badge tone={u.role === 'superadmin' ? 'bad' : u.role === 'admin' ? 'ok' : 'info'}>
                            {u.role}
                          </Badge>
                        </td>
                        <td className="px-5 py-2.5 text-slate-500 tabular-nums text-xs">{u.created_at || '—'}</td>
                        <td className="px-5 py-2.5 text-right">
                          {u.role !== 'superadmin' && (
                            <div className="inline-flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setAssignTarget(u)}>
                                <Cpu className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Assign devices</span>
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => { if (confirm(`Delete user "${u.username}"?`)) deleteUser.mutate(u.id); }}
                                aria-label={`Delete ${u.username}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-bad" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-3">
            <CardTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add user</CardTitle>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" />
            <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" type="password" />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm"
            >
              <option value="admin">admin</option>
              <option value="operator">operator</option>
              <option value="viewer">viewer</option>
            </select>
            <Button
              className="w-full"
              loading={addUser.isPending}
              disabled={!username || !password}
              onClick={() => addUser.mutate()}
            >
              Add user
            </Button>
          </CardBody>
        </Card>
      </div>

      <AssignDevicesModal user={assignTarget} onClose={() => setAssignTarget(null)} />
    </div>
  );
}
